import { useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fileToDataUrl } from "@/lib/format";
import { StatusChip, riderStatusTone } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanInput } from "@/components/BarcodeScanner";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, ImagePlus, RotateCcw, Zap } from "lucide-react";

function PhotoInput({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange(await fileToDataUrl(file));
  };
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-slate-400">{label} *</label>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid={`${testid}-file`} onChange={pick} />
      {value ? (
        <div className="relative">
          <img src={value} alt={label} className="h-36 w-full object-cover rounded-lg border border-emerald-800" data-testid={`${testid}-preview`} />
          <Button type="button" size="sm" variant="outline" data-testid={`${testid}-retake`}
            className="absolute top-2 right-2 h-7 text-xs border-border bg-black/60"
            onClick={() => { onChange(null); if (ref.current) ref.current.value = ""; }}>
            <RotateCcw className="h-3 w-3 mr-1" /> Retake
          </Button>
        </div>
      ) : (
        <button type="button" data-testid={testid} onClick={() => ref.current?.click()}
          className="h-36 w-full rounded-lg border-2 border-dashed border-border hover:border-emerald-700 transition-colors flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-slate-600">
          <ImagePlus className="h-6 w-6" />
          <span className="text-xs">Tap to capture / upload photo</span>
        </button>
      )}
    </div>
  );
}

export default function BatterySwap() {
  const [mobile, setMobile] = useState("");
  const [last4, setLast4] = useState("");
  const [regNo, setRegNo] = useState("");
  const [rider, setRider] = useState(null);
  const [verifyError, setVerifyError] = useState("");
  const [oldSerial, setOldSerial] = useState("");
  const [newSerial, setNewSerial] = useState("");
  const [inwardPhoto, setInwardPhoto] = useState(null);
  const [outwardPhoto, setOutwardPhoto] = useState(null);
  const [odometer, setOdometer] = useState("");
  const [faulty, setFaulty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const blocked = rider?.status === "Swap Blocked";
  const canConfirm = rider && !blocked && oldSerial.trim() && newSerial.trim()
    && inwardPhoto && outwardPhoto && odometer !== "" && !busy;

  const verify = async (e) => {
    e.preventDefault();
    setVerifyError("");
    setRider(null);
    if (!regNo.trim() && (!mobile.trim() || !last4.trim())) {
      setVerifyError("Enter the rider's mobile + chassis last-4, or the vehicle registration number");
      return;
    }
    try {
      const { data } = await api.post("/swap/verify", { mobile, chassis_last4: last4, registration_number: regNo });
      setRider(data);
      if (data.status === "Swap Blocked") toast.error("Rider is Swap Blocked");
    } catch (err) { setVerifyError(errMsg(err)); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/swap/confirm", {
        rider_oid: rider.id, old_serial: oldSerial, new_serial: newSerial,
        odometer_km: parseFloat(odometer), inward_photo: inwardPhoto,
        outward_photo: outwardPhoto, old_battery_faulty: faulty,
      });
      setDone(data);
      toast.success(`Swap ${data.swap_id} confirmed`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const reset = () => {
    setMobile(""); setLast4(""); setRegNo(""); setRider(null); setVerifyError("");
    setOldSerial(""); setNewSerial(""); setInwardPhoto(null); setOutwardPhoto(null);
    setOdometer(""); setFaulty(false); setDone(null);
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto card-tactical p-8 text-center space-y-4" data-testid="swap-success">
        <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto" />
        <h1 className="font-heading text-2xl font-extrabold">Swap Confirmed</h1>
        <p className="text-slate-400 text-sm">
          Swap <span className="font-mono text-slate-700">{done.swap_id}</span> recorded for{" "}
          <span className="text-slate-700">{done.rider_name}</span>. Battery{" "}
          <span className="font-mono text-emerald-400">{done.new_battery_serial}</span> is now In Use.
        </p>
        <Button data-testid="new-swap-btn" onClick={reset}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
          <Zap className="h-4 w-4 mr-2" /> New Swap
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="battery-swap-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Battery Swap</h1>

      <form onSubmit={verify} className="card-tactical p-5 space-y-4" data-testid="swap-verify-form">
        <h2 className="font-heading text-lg font-semibold">1 · Verify Rider</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Rider Mobile Number</label>
            <Input data-testid="rider-lookup-input" value={mobile} onChange={(e) => setMobile(e.target.value)}
              className="bg-slate-50 border-border font-mono" placeholder="10-digit mobile" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Chassis Last 4 Digits</label>
            <Input data-testid="chassis-last4-input" maxLength={4} value={last4}
              onChange={(e) => setLast4(e.target.value)} className="bg-slate-50 border-border font-mono" placeholder="e.g. 2345" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Vehicle Registration No.</label>
            <Input data-testid="reg-no-lookup-input" value={regNo} onChange={(e) => setRegNo(e.target.value)}
              className="bg-slate-50 border-border font-mono" placeholder="e.g. KA01AB1234" />
          </div>
        </div>
        <p className="text-xs text-slate-500">Enter mobile + chassis last-4, or just the vehicle registration number.</p>
        {verifyError && (
          <div className="rounded-lg border border-red-800 bg-red-950/60 px-3 py-2 text-sm text-red-300" data-testid="verify-error">{verifyError}</div>
        )}
        <Button type="submit" data-testid="verify-rider-button" variant="outline" className="border-emerald-800 text-emerald-300">
          Verify Rider
        </Button>
      </form>

      {rider && (
        <div className="card-tactical p-5 space-y-3" data-testid="rider-verified-card">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">{rider.name}</h2>
            <StatusChip label={rider.status} tone={riderStatusTone(rider.status)} testid="swap-rider-status" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm text-slate-400">
            <div>Vehicle: <span className="font-mono text-slate-700">{rider.vehicle?.vehicle_id}</span> (…{rider.vehicle?.chassis_last4})</div>
            <div>Current battery: <span className="font-mono text-slate-700">{rider.battery?.serial_number || "none"}</span></div>
          </div>
          {blocked && (
            <div className="rounded-lg border border-red-700 bg-red-950/70 px-4 py-3 flex items-start gap-3" data-testid="swap-blocked-banner">
              <ShieldAlert className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-red-300 text-sm">SWAP BLOCKED — Payment overdue (&gt;2 days unpaid)</div>
                <div className="text-xs text-red-400/80 mt-0.5">This rider cannot swap batteries until the Admin marks their invoice as paid.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {rider && !blocked && (
        <>
          <div className="card-tactical p-5 space-y-4" data-testid="inward-section">
            <h2 className="font-heading text-lg font-semibold">2 · Inward Battery (returned by rider)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ScanInput label="Scan inward battery barcode" value={oldSerial} onChange={setOldSerial} testid="scan-inward-battery" />
              <PhotoInput label="Inward battery photo" value={inwardPhoto} onChange={setInwardPhoto} testid="inward-photo-upload" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="faulty-checkbox" checked={faulty} onChange={(e) => setFaulty(e.target.checked)}
                className="h-4 w-4 rounded border-border bg-slate-50 accent-red-500" />
              Flag returned battery as Faulty
            </label>
          </div>

          <div className="card-tactical p-5 space-y-4" data-testid="outward-section">
            <h2 className="font-heading text-lg font-semibold">3 · Outward Battery (issued to rider)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ScanInput label="Scan outward battery barcode" value={newSerial} onChange={setNewSerial} testid="scan-outward-battery" />
              <PhotoInput label="Outward battery photo" value={outwardPhoto} onChange={setOutwardPhoto} testid="outward-photo-upload" />
            </div>
            <p className="text-xs text-slate-500">Only batteries with status Available can be issued. Batteries On Hold are not accepted.</p>
          </div>

          <div className="card-tactical p-5 space-y-4" data-testid="confirm-section">
            <h2 className="font-heading text-lg font-semibold">4 · Odometer & Confirm</h2>
            <div className="space-y-1.5 max-w-xs">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Current Odometer (km) *</label>
              <Input data-testid="odometer-input" type="number" min="0" step="0.1" required value={odometer}
                onChange={(e) => setOdometer(e.target.value)} className="bg-slate-50 border-border font-mono" placeholder="e.g. 12450" />
            </div>
            <Button data-testid="confirm-swap-button" disabled={!canConfirm} onClick={confirm}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold text-base">
              {busy ? "Confirming…" : "Confirm Swap"}
            </Button>
            {!canConfirm && !busy && (
              <p className="text-xs text-slate-500 text-center" data-testid="confirm-hint">
                Both barcodes scanned, both photos uploaded and odometer entered are required to enable confirmation.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
